import { type Workplace } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { SpecialQualifications } from "@src/appV2/Qualifications/types";
import { formatISO, parseISO } from "date-fns";

import type { Offer, OpenShift, OpenShiftListItem } from "../../Shift/Open/types";
import type { UserFilters } from "./types";

interface FilterOpenShiftsProps {
  shifts: OpenShiftListItem[];
  offersMap?: Map<string, Offer>;
  workplacesMap: Map<string, Workplace>;
}

function shouldFilterOut<T>(filter: T[] | undefined, value: T) {
  return isDefined(filter) && filter.length > 0 && !filter.includes(value);
}

/**
 * NURSE shifts are shown to workers with either an LVN or RN
 * This is a workaround to make filters work properly
 * https://www.notion.so/TD-Remove-NURSE-Shifts-e3f10a38050741fd9d9beff5d4468fd3
 * CAREGIVER shifts are shown to workers with the CNA qualification.
 */
function getLicenseFilterValues(
  licenses: string[] | undefined,
  customToBaseQualificationNameMap: Map<string, string>
) {
  if (!isDefined(licenses)) {
    return undefined;
  }

  const extraQualifications: string[] = [];

  if (
    [SpecialQualifications.RN, SpecialQualifications.LVN].some((license) =>
      licenses.includes(license)
    )
  ) {
    extraQualifications.push(SpecialQualifications.NURSE);
  }

  if (licenses.includes(SpecialQualifications.CNA)) {
    extraQualifications.push(SpecialQualifications.CAREGIVER);
  }

  for (const license of licenses) {
    const matchingCustomQualificationNames: string[] = [];
    for (const [
      customQualificationName,
      baseQualificationName,
    ] of customToBaseQualificationNameMap) {
      if (baseQualificationName === license) {
        matchingCustomQualificationNames.push(customQualificationName);
      }
    }

    extraQualifications.push(...matchingCustomQualificationNames);
  }

  return [...licenses, ...extraQualifications];
}

function shouldFilterOutByPayPreference(parameters: {
  shift: OpenShift;
  offersMap: Map<string, Offer>;
  pay: number;
}) {
  const { shift, offersMap, pay } = parameters;

  const offerId = shift.relationships?.offer?.data?.id;

  if (!isDefined(offerId)) {
    return false;
  }

  const offer = offersMap.get(offerId);

  if (!isDefined(offer)) {
    return false;
  }

  return offer.attributes.pay.amountInMinorUnits / 100 < pay;
}

function isOpenShift(item: OpenShiftListItem): item is OpenShift {
  // OpenShift objects don't have a type field, but injected items do
  return !("type" in item) || item.type === "open-shift";
}

function shouldIncludeState(
  includedStates: string[] | undefined,
  workplaceId: string,
  workplacesMap: Map<string, Workplace>
) {
  const state = workplacesMap.get(workplaceId)?.attributes.location.state;
  if (!isDefined(includedStates) || includedStates.length === 0) {
    return true;
  }

  return isDefined(state) && includedStates.includes(state);
}

export function filterOpenShifts(
  props: FilterOpenShiftsProps,
  filters: Partial<Omit<UserFilters, "distance">>,
  customToBaseQualificationNameMap: Map<string, string>
): OpenShiftListItem[] {
  const { shifts, offersMap = new Map<string, Offer>(), workplacesMap } = props;
  const { dates, shiftTimeSlots, licenses, pay = 0, includedStates } = filters;

  if (shifts.length === 0) {
    return [];
  }

  return shifts.filter((item) => {
    // Pass through non-shift items (in-app ads, banners, etc.) without filtering
    if (!isOpenShift(item)) {
      return true;
    }

    const shift = item;
    const { qualification, start, timeSlot } = shift.attributes;

    const shiftDate = formatISO(parseISO(start), { representation: "date" });

    if (shouldFilterOut(dates, shiftDate)) {
      return false;
    }

    if (shouldFilterOut(shiftTimeSlots, timeSlot)) {
      return false;
    }

    if (shouldFilterOutByPayPreference({ shift, offersMap, pay })) {
      return false;
    }

    if (!shouldIncludeState(includedStates, shift.relationships.workplace.data.id, workplacesMap)) {
      return false;
    }

    return !shouldFilterOut(
      getLicenseFilterValues(licenses, customToBaseQualificationNameMap),
      qualification
    );
  });
}
